/**
 * Authentication state.
 *
 * Context rather than a store library: the shape is one user object and three
 * actions, and a dependency for that would be ceremony.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react'
import type { ReactNode } from 'react'
import { authApi, SESSION_ENDED, tokens } from '@/services/client'
import { accountApi } from '@/services/endpoints'
import type { User } from '@/types/api'

interface AuthState {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  isStaff: boolean
  login: (email: string, password: string) => Promise<User>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

const STAFF_ROLES = new Set(['editor', 'admin', 'super_admin'])

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  // Starts true only when a token exists — otherwise the whole app would show
  // a spinner on first load for every anonymous visitor.
  const [loading, setLoading] = useState(tokens.isAuthenticated)

  const loadUser = useCallback(async () => {
    if (!tokens.isAuthenticated) {
      setUser(null)
      setLoading(false)
      return
    }
    try {
      setUser(await accountApi.me())
    } catch {
      // A stored token that no longer works is the same as no token.
      tokens.clear()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadUser()
  }, [loadUser])

  // The client dispatches this when a refresh fails mid-flight — the session
  // can end without any component having asked for it.
  useEffect(() => {
    const onSessionEnded = () => setUser(null)
    window.addEventListener(SESSION_ENDED, onSessionEnded)
    return () => window.removeEventListener(SESSION_ENDED, onSessionEnded)
  }, [])

  // A sign-out in one tab must sign the others out too.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'naijaindex.access' && !event.newValue) setUser(null)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login(email, password)
    setUser(result.user)
    return result.user
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout()
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      isStaff: Boolean(user && STAFF_ROLES.has(user.role)),
      login,
      logout,
      refresh: loadUser,
    }),
    [user, loading, login, logout, loadUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.')
  return context
}
